import { cloneDeep } from 'lodash';
import React, { useState, useEffect } from 'react';

import { Loader } from '../../misc/loader-utils';
import { Columns } from '../../base/megatable/column-service';
import StatTable from '../../base/megatable/stat-table';
import PacingIntervalDropdown from '../../base/dropdowns/pacinginterval-dropdown';
import ColumnsDropdown from '../../base/dropdowns/columns-dropdown';
import ReportDropdown from '../../base/dropdowns/report-dropdown';
import Search from '../../ui/search/search';

import { getPacingIntervalOptions } from '../../admin/bid-center/utils';
import { session } from '../../../services/session';
import { api } from '../../../services/api';
import { PublisherDropdown, StatusDropdown } from './multiselect-dropdowns';
import Constants from '../../../app.constants';

import './overview-table.scss';

const defaultPacingOptions = getPacingIntervalOptions();
const defaultStatusOptions = [
  { label: 'Live', name: 'live', selected: true },
  { label: 'Upcoming', name: 'upcoming', selected: true },
  { label: 'Completed', name: 'completed', selected: true },
  { label: 'Paused', name: 'paused', selected: true },
];

const gaCategory = 'Campaign Performance Overview';
const tableName = 'Campaign Overview Table';

function OverviewTable({ merchId, statType, history, match, location, dateRange = {} }) {
  /* State */
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [totals, setTotals] = useState({});
  const [statusOptions, setStatusOptions] = useState(defaultStatusOptions);
  const [pacingOptions, setPacingOptions] = useState(defaultPacingOptions);
  const [publisherOptions, setPublisherOptions] = useState([]);
  const [publisherLabel, setPublisherLabel] = useState('Publisher');
  const [numPubsSelected, setNumPubsSelected] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const tableColumns = new Columns().getColumnsForTable(
    statType,
    session.isOrgAdmin,
    session.isAdminView,
  );
  const defaultColumns = cloneDeep(tableColumns);
  const [columns, setColumns] = useState(tableColumns);

  /* Constants */
  const { orgId } = session;
  const endpoint = `/api/v0/merchants/${orgId}/strategy/overview/stats/`;
  const pubListEndpoint = `/api/v0/merchants/${orgId}/strategy/type_ahead/`;

  /* Effects */
  useEffect(() => {
    fetchPublisherList();
  }, []);

  useEffect(() => {
    //	Only fetch if initial publisher options have been set. Prevents double fetch on load
    if (publisherOptions.length) {
      fetchData();
    }
  }, [statusOptions, pacingOptions, dateRange, publisherOptions, searchQuery]);

  /* Methods */
  async function fetchData() {
    setLoading(true);
    const params = getParams();
    const resp = await api.get(endpoint, params);
    const { stats } = resp.data.data[0];
    const { totals } = resp.data.data[0];
    setStats(stats);
    setTotals(totals);
    setLoading(false);
  }

  async function fetchPublisherList() {
    const resp = await api.get(pubListEndpoint, { type: 'publisher' });
    const pubList = resp.data.data[0].stats;

    const formattedPubs = pubList
      .map(pub => {
        return {
          label: pub.full_name,
          name: pub.pub_id,
          selected: false,
        };
      })
      .sort((pub1, pub2) => {
        return pub1.label.toLowerCase() > pub2.label.toLowerCase() ? 1 : -1;
      });
    setPublisherOptions(formattedPubs);
  }

  function formatOptions(options) {
    if (!options) return [];

    return options.filter(option => option.selected).map(option => option.name);
  }

  function getParams() {
    const selectedPacing = pacingOptions.reduce(
      (acc, curr) => (curr.selected ? acc.concat([`${curr.value[0]}-${curr.value[1]}`]) : acc),
      [],
    );
    const selectedStatuses = formatOptions(statusOptions);
    const selectedPubs = formatOptions(publisherOptions);
    const params = {
      date_from: dateRange.startDate.format('YYYY-MM-DD'),
      date_to: dateRange.endDate.format('YYYY-MM-DD'),
      status: selectedStatuses.join(','),
      pacing_intervals: selectedPacing.join(','),
      publishers_selected: [0, publisherOptions.length].includes(selectedPubs.length)
        ? null
        : selectedPubs.join(','),
      admin_stats: !!session.isAdminView,
    };

    if (statType === 'merchant-performance-overview') {
      params.search_query = searchQuery || null;
    }

    return params;
  }

  function updatePubs(pubs) {
    const selectedPubs = pubs.filter(pub => pub.selected);
    // Sorts pubs by selected and then by name
    const sortedPubs = pubs.sort((a, b) => {
      return a.selected !== b.selected ? (a.selected ? -1 : 1) : a.label > b.label ? 1 : -1;
    });
    const pubLabel =
      selectedPubs.length === 0
        ? 'Publisher'
        : selectedPubs.length > 1
        ? `${selectedPubs.length} Publishers`
        : selectedPubs[0].label;

    setPublisherOptions(sortedPubs);
    setPublisherLabel(pubLabel);
    setNumPubsSelected(selectedPubs.length);
  }

  /* Render */
  const table = !loading ? (
    <StatTable
      stats={stats}
      totals={totals}
      showTotals={!loading}
      columns={columns}
      sortDisabledFields={[]}
      updateSortParams={() => {}}
      statType={statType}
      tableName={null}
      sortOptions={{}}
      disableEditUrl={true}
      history={history}
      match={match}
      location={location}
      classOverride="base-search-table campaign-overview-table"
    ></StatTable>
  ) : null;

  const searchbar =
    statType === 'merchant-performance-overview' ? (
      <div className="table-search-container">
        <Search debounce={true} onSearch={setSearchQuery} />
      </div>
    ) : null;

  const dropdowns = !loading ? (
    <div className="overview-dropdowns">
      {/* Backend for this page is no longer using trigger_ids, which is how we filtered on publishers. */}
      {/* Temporarily hiding this until we update backend with the ability to filter on publishers with triggers */}
      {/* <PublisherDropdown */}
      {/*  gaEvent={{ */}
      {/*    category: gaCategory, */}
      {/*    label: 'Publisher Dropdown', */}
      {/*  }} */}
      {/*  items={publisherOptions} */}
      {/*  updateOptions={updatePubs} */}
      {/*  staticFields={[]} */}
      {/*  allSelected={numPubsSelected === publisherOptions.length} */}
      {/*  numSelected={numPubsSelected} */}
      {/*  label={publisherLabel} */}
      {/* /> */}
      <StatusDropdown
        gaEvent={{
          category: gaCategory,
          label: 'Campaign Statuses Dropdown',
        }}
        items={statusOptions}
        updateOptions={setStatusOptions}
      />
      <PacingIntervalDropdown
        gaEvent={{
          category: gaCategory,
          label: 'Pacing Interval Dropdown',
        }}
        items={pacingOptions}
        updatePacingIntervalOptions={setPacingOptions}
      />
      <ColumnsDropdown
        gaEvent={{
          category: gaCategory,
          label: `Column Dropdown`,
        }}
        items={columns}
        updateColumns={setColumns}
        resetColumns={() => setColumns(defaultColumns)}
        statType={statType}
        staticFields={[]}
      />
      <ReportDropdown
        statType={statType}
        orgId={orgId}
        gaEvent={{
          category: gaCategory,
          label: `${tableName} Export Data`,
        }}
        queryParams={getParams()}
        columns={columns}
        // currentPage={page}
        // perPage={perPage}
        rowCounts={{ visible: stats.length, all: stats.length }}
        disabled={loading}
        reportVersion="V2"
        endpoint={endpoint}
        reportOptionsOverride={[{ label: 'All Rows', value: 'all' }]}
      />
    </div>
  ) : null;

  return (
    <div className="campaign-overview-table">
      <div className="table-filters">
        {searchbar}
        {dropdowns}
      </div>
      {table}
      <Loader loading={loading} />
    </div>
  );
}

export default OverviewTable;
